// src/components/ui/2-organisms/StorefrontRingGrid/StorefrontRingGrid.js
import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { clsx } from 'clsx';
import styles from './StorefrontRingGrid.module.css';
import GalleryDots from '../../0-atoms/GalleryDots/GalleryDots';
import { getOptimizedImageUrl } from '../../../../utils/imageUtils';

const DEFAULT_RING_IMAGE = 'https://placehold.co/300x300/eee/ccc?text=Ring';

/**
 * A 2-column grid for displaying rings on a jeweller's storefront.
 *
 * @param {object} props
 * @param {Array<object>} props.rings - The array of ring objects to display.
 * @param {function(object): void} props.onRingClick - Callback when a ring is clicked.
 */
const StorefrontRingGrid = ({ rings, onRingClick }) => {
  const pointerDataRef = useRef({});
  const imageRefs = useRef({});
  const [imageStatusMap, setImageStatusMap] = useState({});
  const [activeImages, setActiveImages] = useState({});
  const [dragOffsets, setDragOffsets] = useState({});

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(price).replace('.00', '');
  };

  // Helper function to get the first image URL from various possible fields
  const getImageSources = useCallback((ring) => {
    if (Array.isArray(ring.imageUrls) && ring.imageUrls.length > 0) {
      return ring.imageUrls;
    }
    if (Array.isArray(ring.images) && ring.images.length > 0) {
      return ring.images;
    }
    if (ring.imageUrl) return [ring.imageUrl];
    if (ring.image_url) return [ring.image_url];
    if (ring.image) return [ring.image];
    return [DEFAULT_RING_IMAGE];
  }, []);

  // Helper function to get ring name from various possible fields
  const getRingName = (ring) => {
    console.log('Ring data:', ring); // Log the ring object for debugging
    
    // Check metadata.name first (jeweller products structure)
    if (ring.metadata?.name) {
      return ring.metadata.name;
    }
    
    // Fallback to other possible field names for backwards compatibility
    return ring.ringName || ring.name || ring.title || 'Untitled Ring';
  };

  const normalizeSource = useCallback((source) => {
    if (!source) {
      return {
        primary: DEFAULT_RING_IMAGE,
        fallback: DEFAULT_RING_IMAGE,
      };
    }

    if (typeof source === 'string') {
      return {
        primary: source,
        fallback: source,
      };
    }

    if (typeof source === 'object') {
      const candidate =
        source.optimizedUrl ||
        source.imageUrl ||
        source.url ||
        source.src ||
        source.thumbnail ||
        DEFAULT_RING_IMAGE;

      const fallbackCandidate =
        source.fallbackUrl ||
        source.originalUrl ||
        source.imageUrl ||
        source.url ||
        source.src ||
        source.thumbnail ||
        candidate ||
        DEFAULT_RING_IMAGE;

      return {
        primary: candidate || DEFAULT_RING_IMAGE,
        fallback: fallbackCandidate || DEFAULT_RING_IMAGE,
      };
    }

    return {
      primary: DEFAULT_RING_IMAGE,
      fallback: DEFAULT_RING_IMAGE,
    };
  }, []);

  const preparedRings = useMemo(() => {
    return rings.map((ring) => {
      const sources = getImageSources(ring);
      const normalized = (sources.length ? sources : [DEFAULT_RING_IMAGE]).map((source, index) => {
        const { primary, fallback } = normalizeSource(source);
        const optimizedUrl = getOptimizedImageUrl(primary, 'SMALL');
        return {
          key: `${ring.id}-${index}`,
          optimizedUrl,
          fallbackUrl: fallback || DEFAULT_RING_IMAGE,
        };
      });

      return {
        ring,
        images: normalized,
      };
    });
  }, [rings, getImageSources, normalizeSource]);

  useEffect(() => {
    const validRingIds = new Set(preparedRings.map(({ ring }) => ring.id));

    setImageStatusMap((prev) => {
      const nextStatus = {};
      let changed = false;

      preparedRings.forEach(({ ring, images }) => {
        const refs = imageRefs.current[ring.id] || [];
        const previous = prev[ring.id] || [];
        const statuses = images.map((_, index) => {
          const img = refs[index];
          if (img && img.complete) {
            return img.naturalWidth > 0 ? 'loaded' : 'error';
          }
          return previous[index] || 'loading';
        });

        nextStatus[ring.id] = statuses;
        if (
          statuses.length !== previous.length ||
          statuses.some((status, idx) => status !== previous[idx])
        ) {
          changed = true;
        }
      });

      return changed ? nextStatus : prev;
    });

    Object.keys(imageRefs.current).forEach((ringId) => {
      if (!validRingIds.has(ringId)) {
        delete imageRefs.current[ringId];
      }
    });
  }, [preparedRings]);

  const updateImageStatus = useCallback((ringId, index, status) => {
    setImageStatusMap((prev) => {
      const existing = prev[ringId] ? [...prev[ringId]] : [];
      if (existing[index] === status) return prev;
      existing[index] = status;
      return { ...prev, [ringId]: existing };
    });
  }, []);

  const handleImageLoad = useCallback((ringId, index) => {
    updateImageStatus(ringId, index, 'loaded');
  }, [updateImageStatus]);

  const handleImageError = useCallback((ringId, index, fallbackUrl) => {
    const refs = imageRefs.current[ringId] || [];
    const img = refs[index];
    if (img && fallbackUrl && img.src !== fallbackUrl) {
      updateImageStatus(ringId, index, 'loading');
      img.src = fallbackUrl;
      return;
    }
    updateImageStatus(ringId, index, 'error');
  }, [updateImageStatus]);

  const updateActiveIndex = useCallback((ringId, nextIndex, total) => {
    setActiveImages(prev => {
      const clamped = Math.min(Math.max(nextIndex, 0), total - 1);
      if (prev[ringId] === clamped) return prev;
      return { ...prev, [ringId]: clamped };
    });
    setDragOffsets(prev => {
      if (prev[ringId] === 0 || prev[ringId] === undefined) return prev;
      return { ...prev, [ringId]: 0 };
    });
  }, []);

  const registerPointerStart = useCallback((ringId, clientX, clientY) => {
    pointerDataRef.current[ringId] = {
      startX: clientX,
      startY: clientY,
      lastX: clientX,
      isSwiping: false,
      preventClick: false,
    };
    setDragOffsets(prev => ({ ...prev, [ringId]: 0 }));
  }, []);

  const handlePointerMove = useCallback((ringId, clientX, clientY) => {
    const data = pointerDataRef.current[ringId];
    if (!data) return;
    const deltaX = clientX - data.startX;
    const deltaY = clientY - data.startY;
    // Determine if horizontal swipe
    if (!data.isSwiping && Math.abs(deltaX) > 10 && Math.abs(deltaX) > Math.abs(deltaY)) {
      data.isSwiping = true;
      data.preventClick = true;
    }
    if (data.isSwiping) {
      data.lastX = clientX;
      setDragOffsets(prev => {
        if (prev[ringId] === deltaX) return prev;
        return { ...prev, [ringId]: deltaX };
      });
    }
  }, []);

  const finalizeSwipe = useCallback((ringId, imageCount) => {
    const data = pointerDataRef.current[ringId];
    if (!data) return;
    if (data.isSwiping) {
      const totalDelta = data.lastX - data.startX;
      const threshold = 30;
      const isSwipe = Math.abs(totalDelta) >= threshold;
      if (isSwipe) {
        setActiveImages(prev => {
          const current = prev[ringId] ?? 0;
          const next = totalDelta < 0 ? current + 1 : current - 1;
          const clamped = Math.min(Math.max(next, 0), imageCount - 1);
          if (clamped === current) return prev;
          return { ...prev, [ringId]: clamped };
        });
      }
      data.preventClick = isSwipe;
      setDragOffsets(prev => ({ ...prev, [ringId]: 0 }));
    }
    // cleanup but keep preventClick to suppress immediate click
    pointerDataRef.current[ringId] = {
      ...pointerDataRef.current[ringId],
      isSwiping: false,
      swipeHandled: true,
    };
  }, []);

  const clearPointer = useCallback((ringId, delay = 0) => {
    if (!pointerDataRef.current[ringId]) return;
    const clear = () => {
      delete pointerDataRef.current[ringId];
      setDragOffsets(prev => {
        if (prev[ringId] === undefined) return prev;
        const next = { ...prev };
        delete next[ringId];
        return next;
      });
    };
    if (delay) {
      setTimeout(clear, delay);
    } else {
      clear();
    }
  }, []);

  const handleClick = useCallback((ring) => {
    const pointerState = pointerDataRef.current[ring.id];
    if (pointerState?.preventClick) {
      clearPointer(ring.id, 50);
      return;
    }
    onRingClick(ring);
  }, [clearPointer, onRingClick]);

  return (
    <div className={styles.grid}>
      {preparedRings.map(({ ring, images }) => {
        imageRefs.current[ring.id] = imageRefs.current[ring.id] || [];
        const imageCount = images.length;
        const activeIndex = activeImages[ring.id] ?? 0;
        const ringName = getRingName(ring);
        const dragOffset = dragOffsets[ring.id] ?? 0;
        const isDragging = pointerDataRef.current[ring.id]?.isSwiping || false;
        const imageStatuses = imageStatusMap[ring.id] || [];
        const sliderStyle = {
          transform: `translateX(calc(${activeIndex * -100}% + ${dragOffset}px))`,
          transition: isDragging ? 'none' : 'transform 0.35s ease',
        };
        const pointerHandlers = {
          onTouchStart: (e) => {
            if (e.touches.length !== 1) return;
            registerPointerStart(ring.id, e.touches[0].clientX, e.touches[0].clientY);
          },
          onTouchMove: (e) => {
            if (e.touches.length !== 1) return;
            handlePointerMove(ring.id, e.touches[0].clientX, e.touches[0].clientY);
          },
          onTouchEnd: () => {
            finalizeSwipe(ring.id, imageCount);
            clearPointer(ring.id, 50);
          },
          onTouchCancel: () => {
            clearPointer(ring.id);
          },
          onMouseDown: (e) => registerPointerStart(ring.id, e.clientX, e.clientY),
          onMouseMove: (e) => {
            if (e.buttons !== 1) return;
            handlePointerMove(ring.id, e.clientX, e.clientY);
          },
          onMouseUp: () => {
            finalizeSwipe(ring.id, imageCount);
            clearPointer(ring.id, 50);
          },
          onMouseLeave: () => {
            finalizeSwipe(ring.id, imageCount);
            clearPointer(ring.id);
          }
        };
        
        return (
          <button
            key={ring.id}
            className={styles.card}
            onClick={() => handleClick(ring)}
            type="button"
          >
            <div
              className={styles.imageWrapper}
              {...pointerHandlers}
              role="presentation"
            >
              <div className={styles.slider} style={sliderStyle}>
                {images.map((image, idx) => (
                  <div className={styles.slide} key={idx}>
                    <img 
                      ref={(node) => {
                        const refs = imageRefs.current[ring.id] || [];
                        refs[idx] = node || null;
                        imageRefs.current[ring.id] = refs;
                      }}
                      src={image.optimizedUrl} 
                      alt={`${ringName} view ${idx + 1}`} 
                      className={clsx(styles.image, {
                        [styles.imageLoaded]: imageStatuses[idx] === 'loaded',
                        [styles.imageError]: imageStatuses[idx] === 'error',
                        [styles.imageLoading]: imageStatuses[idx] === 'loading',
                      })}
                      loading="lazy"
                      onLoad={() => handleImageLoad(ring.id, idx)}
                      onError={() => handleImageError(ring.id, idx, image.fallbackUrl)}
                    />
                  </div>
                ))}
              </div>
              {/* Show dots only if there are multiple images */}
              {imageCount > 1 && (
                <GalleryDots
                  count={imageCount}
                  activeIndex={activeIndex}
                  className={styles.dots}
                  onDotClick={(index) => updateActiveIndex(ring.id, index, imageCount)}
                  maxVisible={7}
                />
              )}
            </div>
            <div className={styles.info}>
              <p className={styles.name}>{ringName}</p>
              <p className={styles.price}>{formatPrice(ring.price || 0)}</p>
            </div>
          </button>
        );
      })}
    </div>
  );
};

export default StorefrontRingGrid;
